import React, { useContext, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { MarketingConsentInformation } from "../../model/MarketingConsentType";
import { ZahtjevContext } from "../../common/context/ZahtjevProvider";
import { CreateZahtjevMarketinskaPrivolaRequest, useUpdateMarketingConsent } from "../../api/RegistrationApi";
import { useNotification } from "../../common/context/NotificationProvider";
import { Formik, FormikErrors, FormikValues, useFormikContext } from "formik";
import { Button } from "../common/Button";
import Switch from "../common/Switch";
import { Card } from "../common/Card";
import { ERROR_MESSAGE_NOTIFICATION } from "../../common/constants/notificationConstants";
import { MARKETING_CONSENT_INFORMATION_STATUS } from "../../common/constants/navigationConstants";
import { Tooltip } from "../common/Tooltip";
import { useTranslation } from "react-i18next";
import { SupportedLanguage } from "../../i18n";
import { DropDownMultipleField } from "../common/DropDownMultipleField";
import { SIFARNIK_KANALI_DOSTAVE } from "../../common/data/sifarnik";
import { Sifarnik } from "../../model/FinancialInformationType";
import { validateField, validateForm } from "./marketingConsentValidation";
import { scrollToFirstError } from "../../util/formUtil";

const PDF_FILENAME_HR = 'Privola za obradu osobnih podataka.pdf';
const PDF_URL_HR = `/kyc2025/data/${PDF_FILENAME_HR}`;

// TODO: Add UPDATED English PDF
const PDF_FILENAME_EN = 'Consent to personal data processing.pdf';
const PDF_URL_EN = `/kyc2025/data/${PDF_FILENAME_EN}`;

const INITIAL_MARKETING_CONSENT_INFORMATION_FORMIK_VALUES: MarketingConsentInformation = {
    privolaMarketinskeInformacije: false,
    privolaPersonaliziranePonude: false,
    kanaliDostave: [],
} as MarketingConsentInformation;

const hasAnyConsent = (values: MarketingConsentInformation) =>
    values.privolaMarketinskeInformacije || values.privolaPersonaliziranePonude;

const ChannelsValidateOnMount = () => {
    const { values, setFieldError } = useFormikContext<MarketingConsentInformation>();

    useEffect(() => {
        if (hasAnyConsent(values)) {
            validateField("kanaliDostave", values.kanaliDostave ?? null, setFieldError);
        }
    }, []);

    return null;
};

function mapMarketingConsentInformationToCreateZahtjevMarketinskaPrivolaRequest(input: MarketingConsentInformation): CreateZahtjevMarketinskaPrivolaRequest {
    return {
        privolaMarketinskeInformacije: input.privolaMarketinskeInformacije ?? false,
        privolaPersonaliziranePonude: input.privolaPersonaliziranePonude ?? false,
        kanaliDostave: hasAnyConsent(input)
          ? (input.kanaliDostave?.map((k: Sifarnik) => k.id).join(',') ?? '')
          : '',
    }
}

const MarketingConsentPage = () => {
    const { t, i18n } = useTranslation();
    const lng = i18n.resolvedLanguage as SupportedLanguage;
    const {
        marketingConsent,
        marketingConsentCallback,
        contactAddressInformation,
        getZahtjevId,
        restoreContext,
        getStatus
    } = useContext(ZahtjevContext);
    const {
        isPending: isLoading,
        mutate: updateMarketingConsentMutation
    } = useUpdateMarketingConsent(getZahtjevId());
    const currentStatus = getStatus();
    const pageStatus = MARKETING_CONSENT_INFORMATION_STATUS;
    const navigate = useNavigate();
    const { notify } = useNotification();
    const hasPhone = !!(contactAddressInformation.telefonPredbroj && contactAddressInformation.telefon);

    const initialMarketingConsentInformationFormValues = Object.keys(marketingConsent).length !== 0
      ? {
          ...marketingConsent,
          kanaliDostave: hasPhone
            ? marketingConsent.kanaliDostave
            : marketingConsent.kanaliDostave?.filter((k: Sifarnik) => k.id !== "P"),
      }
      : INITIAL_MARKETING_CONSENT_INFORMATION_FORMIK_VALUES;
    const deliveryChannelOptions = hasPhone
      ? SIFARNIK_KANALI_DOSTAVE
      : SIFARNIK_KANALI_DOSTAVE.filter(option => option.id !== "P");
    const lastChannelsRef = useRef<Sifarnik[]>(deliveryChannelOptions.filter(o => o.id === "E" || o.id === "M"));

    const handleView = () => {
        try {
            const pdfUrl = lng === 'en' ? PDF_URL_EN : PDF_URL_HR;
            window.open(pdfUrl, '_blank');
        } catch (err) {
            console.error("Failed to open PDF:", err);
        }
    };

    const handleSubmit = (values: MarketingConsentInformation, setFieldError: (field: string, message: (string | undefined)) => void) => {
        const isFormValid = validateForm(values, setFieldError);

        if (isFormValid) {
            const request = mapMarketingConsentInformationToCreateZahtjevMarketinskaPrivolaRequest(values);

            updateMarketingConsentMutation(request, {
                onSuccess: () => {
                    marketingConsentCallback(values)
                    navigate("/summary");
                },
                onError: () => {
                    notify(ERROR_MESSAGE_NOTIFICATION, "error");
                }
            });
        } else {
            scrollToFirstError();
        }
    };

    const handleReturn = (values: MarketingConsentInformation, dirty: boolean) => {
        if (dirty) marketingConsentCallback(values);
        navigate('/documentation-delivery', { state: { isBack: true } });
    };

    const handleValidation = (setFieldError: (field: string, message: (string | undefined)) => void) =>
      (value: any, field?: string) => {
          return validateField(field!, value, setFieldError);
      };

    const handleSetFieldValue = (
      setFieldValue: (field: string, value: any, shouldValidate?: boolean) => Promise<void | FormikErrors<FormikValues>>,
      values?: MarketingConsentInformation,
      setFieldError?: (field: string, message: (string | undefined)) => void,
    ) => (value: any, field?: string) => {
        if (values && setFieldError && (field === "privolaMarketinskeInformacije" || field === "privolaPersonaliziranePonude")) {
            const newValues = { ...values, [field]: value };
            if (!hasAnyConsent(newValues)) {
                lastChannelsRef.current = values.kanaliDostave ?? [];
                setFieldValue("kanaliDostave", [], false);
                setFieldError("kanaliDostave", undefined);
            } else if (!hasAnyConsent(values)) {
                setFieldValue("kanaliDostave", lastChannelsRef.current, false);
                validateField("kanaliDostave", lastChannelsRef.current, setFieldError);
            }
        }
        return setFieldValue(field!, value, false);
    };

    useEffect(() => {
        restoreContext(pageStatus);
    }, []);

    return (
      <Card title={<div className="flex items-center"><span>{t("marketing-consent-page.title")}</span><Tooltip
        text={t("marketing-consent-page.tooltip")} className="ml-2 cursor-help" iconSize="medium"/></div>}
            isLoading={parseInt(currentStatus) < parseInt(pageStatus)}>
          <Formik
            initialValues={initialMarketingConsentInformationFormValues}
            onSubmit={(values, { setFieldError }) => handleSubmit(values, setFieldError)}
          >{({ values, errors, handleSubmit, setFieldError, setFieldValue, dirty }) => (
            <form noValidate onSubmit={handleSubmit} autoComplete="off">
                <ChannelsValidateOnMount />
                <p>
                    {t("marketing-consent-page.description-first")}
                </p>

                <br/>
                <p>
                    {t("marketing-consent-page.description-third")}
                </p>

                <Button className="my-8" type="button" text={t("marketing-consent-page.button-view-document")}
                        onClick={handleView}/>

                <div className="space-y-5 flex flex-col">
                    <Switch
                      name="privolaMarketinskeInformacije"
                      label={t("marketing-consent-page.marketing-consent-field.label")}
                      defaultValue={values.privolaMarketinskeInformacije}
                      handleValueChange={handleSetFieldValue(setFieldValue, values, setFieldError)}
                    />

                    <Switch
                      name="privolaPersonaliziranePonude"
                      label={t("marketing-consent-page.personalized-offer-field.label")}
                      defaultValue={values.privolaPersonaliziranePonude}
                      handleValueChange={handleSetFieldValue(setFieldValue, values, setFieldError)}
                    />

                    {hasAnyConsent(values) && (
                      <DropDownMultipleField
                        name="kanaliDostave"
                        label={t("marketing-consent-page.delivery-channels-field.label")}
                        options={deliveryChannelOptions}
                        value={values.kanaliDostave}
                        error={t(errors?.kanaliDostave as string)}
                        getOptionLabel={(option: Sifarnik) => t(option.vrijednost)}
                        getOptionValue={(option: Sifarnik) => option.id}
                        handleValueChange={handleSetFieldValue(setFieldValue)}
                        handleValidation={handleValidation(setFieldError)}
                      />
                    )}
                </div>

                <div className="mt-10 text-center md:text-left">
                    <Button
                      onClick={() => handleReturn(values, dirty)}
                      type="button"
                      text={t("common.button-previous")}
                      variant="secondary"
                      disabled={false}
                    />
                    <Button
                      type="submit"
                      text={t("common.button-next")}
                      disabled={isLoading}
                    />
                </div>
            </form>
          )}
          </Formik>
      </Card>
    );
};

export default MarketingConsentPage;